/**
 * The audit lines of AutoMod (2026-10-29): rule changes by a person, and
 * what a rule did (no person: "AutoMod blocked a message from …").
 */
import { AuditLogEvent, type AuditLogEntry } from '@neosocial/protocol';
import type { Translator } from '../i18n';

export const AUTOMOD_AUDIT_EVENTS: ReadonlySet<number> = new Set([
  AuditLogEvent.AUTO_MODERATION_RULE_CREATE,
  AuditLogEvent.AUTO_MODERATION_RULE_UPDATE,
  AuditLogEvent.AUTO_MODERATION_RULE_DELETE,
  AuditLogEvent.AUTO_MODERATION_BLOCK_MESSAGE,
  AuditLogEvent.AUTO_MODERATION_FLAG_TO_CHANNEL,
  AuditLogEvent.AUTO_MODERATION_USER_COMMUNICATION_DISABLED,
]);

function ruleOf(e: AuditLogEntry): string {
  const named = e.changes?.find((c) => c.key === 'name');
  return (
    e.options?.auto_moderation_rule_name ??
    (named?.new_value as string | undefined) ??
    (named?.old_value as string | undefined) ??
    '—'
  );
}

/** The sentence for an AutoMod entry; `who` holds the names auditText already worked out. */
export function automodAuditLine(
  tr: Translator,
  e: AuditLogEntry,
  who: { actor: string; target: string; channel: string; time(v: unknown): string },
): string {
  const { t } = tr;
  const rule = ruleOf(e);
  const v = { actor: who.actor, target: who.target, channel: who.channel, rule };
  switch (e.action_type as number) {
    case AuditLogEvent.AUTO_MODERATION_RULE_CREATE:
      return t('am.audit.create', v);
    case AuditLogEvent.AUTO_MODERATION_RULE_UPDATE:
      return t('am.audit.update', v);
    case AuditLogEvent.AUTO_MODERATION_RULE_DELETE:
      return t('am.audit.delete', v);
    case AuditLogEvent.AUTO_MODERATION_BLOCK_MESSAGE:
      return t('am.audit.block', v);
    case AuditLogEvent.AUTO_MODERATION_FLAG_TO_CHANNEL:
      return t('am.audit.flag', v);
    default: {
      const until = e.changes?.find((c) => c.key === 'communication_disabled_until')?.new_value;
      return t('am.audit.timeout', { ...v, time: who.time(until) });
    }
  }
}
